import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import styled from '@emotion/styled'

import { formatFOIDetails, formatFOIForSingleForm } from '../../util/foiFormatting'
import { FlexColumn, FlexEndContainer, InlineContainer, InputWrapperDiv, LightBGContainer, SpaceBetweenRowContainer } from '../styles/Containers'
import { TextField, SelectMenuItem } from '../styles/TextField'
import { BoldFormTitle } from '../styles/Text'
import { SecondaryButton } from '../common/Button'
import FOIRequest from '../foi/FOIRequest'
import RequestClassification from './RequestClassification'
import InternalSummary from './InternalSummary'
import EditableFOIRequestInfo from './EditableFOIRequestInfo'

const ContentContainer = styled.div`
  display: flex;
  gap: 15px;
`

const RequestContainer = styled(LightBGContainer)`
  padding: 20px 30px 30px 30px;
`

const OriginalRequestContainer = styled(LightBGContainer)`
  padding: 20px 30px 30px;
  display: flex;
  gap: 20px;
  flex-direction: column;
`

const StyledBoldFormTitle = styled(BoldFormTitle)`
  line-height: 36px;
`

function FOIRequestView({
  foiId,
  foiRequest,
  foiVersions,
  internalSummary,
  requestClassification,
  allKeywords,
  allDepartments,
  editable,
  onUpdateSummary = () => {},
  onUpdateClassification = () => {},
  onUpdateKeywords = () => {},
  onUpdateFoiVersion = () => {},
  onSelectFoiVersion = () => {}
}) {
  const [isEditingRequest, setIsEditingRequest] = useState(false)
  const [selectedVersion, setSelectedVersion] = useState()

  const handleCancelEditRequest = () => setIsEditingRequest(false)
  function handleEditRequest(data) {
    onUpdateFoiVersion(data)
    setIsEditingRequest(false)
  }

  function handleUpdateSelectedVersion(version) {
    setSelectedVersion(version)
    onSelectFoiVersion(version.id)
  }

  useEffect(() => {
    if (foiRequest) setSelectedVersion(foiVersions.find(v => v.id === foiRequest.id))
  }, [foiRequest, foiVersions])

  return (
    <ContentContainer>
      <FlexColumn width="50%">
        <InlineContainer>
          <OriginalRequestContainer>
            {!isEditingRequest ? (
              <>
                <SpaceBetweenRowContainer>
                  <StyledBoldFormTitle>Request</StyledBoldFormTitle>
                  {selectedVersion && (
                    <FlexEndContainer>
                      <InputWrapperDiv minwidth="210px">
                        <TextField select haslabel="false" inputProps={{ 'aria-label': 'Version' }} value={selectedVersion.id}>
                          {foiVersions.map(option => (
                            <SelectMenuItem key={option.id} value={option.id} onClick={() => handleUpdateSelectedVersion(option)}>
                              {option.name}
                            </SelectMenuItem>
                          ))}
                        </TextField>
                      </InputWrapperDiv>
                      <SecondaryButton
                        onClick={() => setIsEditingRequest(true)}
                        disabled={!selectedVersion.isMostRecent || !editable || selectedVersion.id !== foiRequest.id}>
                        Edit
                      </SecondaryButton>
                    </FlexEndContainer>
                  )}
                </SpaceBetweenRowContainer>
                <FOIRequest foiRequest={formatFOIDetails(foiRequest)} order="requestFirst" />
              </>
            ) : (
              <EditableFOIRequestInfo
                foiId={foiId}
                requestInfo={formatFOIForSingleForm(foiRequest)}
                versionName={selectedVersion.name}
                onSuccess={handleEditRequest}
                handleCancel={handleCancelEditRequest}
              />
            )}
          </OriginalRequestContainer>
        </InlineContainer>
      </FlexColumn>
      <FlexColumn width="50%">
        <RequestContainer>
          <InternalSummary foiId={foiId} internalSummary={internalSummary} onUpdateSummary={onUpdateSummary} />
        </RequestContainer>
        <RequestContainer>
          <RequestClassification
            foiId={foiId}
            requestClassification={requestClassification}
            allKeywords={allKeywords}
            allDepartments={allDepartments}
            onUpdateClassification={onUpdateClassification}
            onUpdateKeywords={onUpdateKeywords}
          />
        </RequestContainer>
      </FlexColumn>
    </ContentContainer>
  )
}

FOIRequestView.propTypes = {
  foiId: PropTypes.string.isRequired,
  foiRequest: PropTypes.object.isRequired,
  foiVersions: PropTypes.array.isRequired,
  internalSummary: PropTypes.array.isRequired,
  requestClassification: PropTypes.object.isRequired,
  allKeywords: PropTypes.array.isRequired,
  allDepartments: PropTypes.array.isRequired,
  editable: PropTypes.bool.isRequired,
  onUpdateSummary: PropTypes.func,
  onUpdateClassification: PropTypes.func,
  onUpdateKeywords: PropTypes.func,
  onUpdateFoiVersion: PropTypes.func,
  onSelectFoiVersion: PropTypes.func
}

export default FOIRequestView
